import { Box, Divider, ThemeProvider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import theme from '../../theme';
import CoiChat from './CoiChat';
import CoiUseCase from './CoiUseCase';
import {
  analyzeFile,
  ChatMessage,
  getChatHistory,
  getUseCaseDetails,
  postQuery,
} from './IdeationService';
import { setVersionedReports } from '../IdeationXPLR/ideationFormSlice';

import { opportunityDetailsFieldKeyMap } from '../NewIdeationXPLR/NewIdeation/OpportunityDetails';
import { OpportunityDetailFieldKey } from '../NewIdeationXPLR/hooks/useOpportunityDetailsForm';
import {
  chatBotFieldMap,
  fieldAPIKeyMap,
  fieldLabelMap,
} from '../NewIdeationXPLR/NewIdeation/constants';
import { RootState } from '../../store';
import ShowToast from '../../common/components/ShowToast';
import { ToastTypeEnum } from '../../constants/solutionXPLR';
import {
  NewIdeationState,
  updateIdeationFieldState,
} from '../../slices/ideationSlice/newIdeationSlice';
import { compareVersions } from '../../common/utils';
import ChatListingSidebar from './ChatListingSidebar';

import { getLatestQueryHistory } from '../IdeationCOI/IdeationService';
import ProcessCreation from '../NewIdeationXPLR/ProcessCreation';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface Process {
  id: string;
  name: string;
  date: string;
  subtitle?: string;
  context_knowledge?: Record<string, string>;
}

interface UploadedFile {
  id: string;
  name: string;
  file: File;
  url?: string;
  isUploading?: boolean;
  isAnalyzing?: boolean;
  fileData?: string;
  fileSize?: string;
  finalSummary?: string;
  extractedData?: any[];
  filePath?: string;
}

interface ProcessCreationState {
  details: string;
  uploadedFiles: UploadedFile[];
  selectedContexts: string[];
  recentProcesses: Process[];
  isLoadingRecents: boolean;
  contextProcesses: Process[];
  isLoadingContext: boolean;
  fileSummary: string;
}

const categoryFieldMap = (category: string) => {
  switch (category) {
    case 'currentProcess':
      return 'Current Process';
    case 'expectedImpact':
      return 'Expected Impact';
    case 'specificIdeasOfImprovement':
      return 'Specific Ideas of Improvement';
    case 'keyPerformanceIndicators':
      return 'Key Performance Indicators';
    default:
      return category;
  }
};

interface IdeationChatbotProps {
  setActiveStep?: (step: number) => void;
  field?: OpportunityDetailFieldKey;
  handleBack?: () => void;
}

const DataXPLR: React.FC<IdeationChatbotProps> = ({
  field: defaultField = opportunityDetailsFieldKeyMap.currentProcess,
  handleBack = () => {},
}) => {
  const { id: initialChatId = '' } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const organization_id = localStorage.getItem('current_organization') || '';

  // Shared state for version synchronization
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  // State for resizable chat width
  const [chatWidth, setChatWidth] = useState<number>(600);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<Record<string, boolean>>({});
  const [versionData, setversionData] = useState<Record<string, any>>({});
  const [useCaseDetails, setUseCaseDetails] = useState<Record<string, any>>({});
  const [fileSummary, setFileSummary] = useState<string>('');
  const [contextState, setContextState] = useState<ProcessCreationState>({
    details: '',
    uploadedFiles: [],
    selectedContexts: [],
    recentProcesses: [],
    isLoadingRecents: true,
    contextProcesses: [],
    isLoadingContext: false,
    fileSummary: '',
  });
  // const [selectValue, setSelectValue] = useState('current_process');
  const [allContextData, setAllContextData] = useState<any[]>([]);
  const [allFilesData, setAllFilesData] = useState<any[]>([]);
  const [, setIsUseDataXplrModalOpen] = useState(false);
  const [isSendingToSolutionXPLR, setIsSendingToSolutionXPLR] = useState(false);
  const [editorContent, setEditorContent] = useState<string>('');
  const [chatSessions, setChatSessions] = useState<any[]>([]);
  const [chatId, setChatId] = useState<string>('');
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [showKnowledgeCreation, setShowKnowledgeCreation] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>(
    categoryFieldMap(location?.state?.selectedCategory || '') ||
      categoryFieldMap(fieldLabelMap[searchParams.get('selectedCategory') || '']) ||
      'All'
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentField, setCurrentField] = useState(
    searchParams.get('selectedCategory') || defaultField
  );
  const [currentLimit, setCurrentLimit] = useState<number>(20);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState('newest_first');
  const [failedMessageData, setFailedMessageData] = useState<{
    message: string;
    files: any[];
    contexts: any[];
  } | null>(null);
  const skipChatLoadRef = useRef(false);

  const {
    detailsText,
    selectedContexts,
    taxonomy,
    uploadedFiles,
    finalSummary,
    // currentProcess,
    // expectedImpact,
    // specificIdeasOfImprovement,
    recent_imported_chat,
  } = useSelector((state: RootState) => state.newIdeation);

  // Handle version change from either component
  const handleVersionChange = (version: string, versionFileSummary: string = '') => {
    console.log('Version changed to:', version);
    setCurrentVersion(version);

    if (versionFileSummary) setFileSummary(versionFileSummary);
  };

  // Handle chat width change
  const handleChatWidthChange = (width: number) => {
    setChatWidth(width);
  };

  const isValidAnalysisResponse = (res: any) => {
    return (
      res &&
      typeof res === 'object' &&
      typeof res.final_summary === 'string' &&
      res.final_summary.trim().length > 0 &&
      Array.isArray(res.extracted_data) &&
      res.extracted_data.length > 0 &&
      res.extracted_data.every((x: any) => x.fileData || x.fileUrl)
    );
  };

  const handleSendMessage = async (
    query?: string,
    isFirstMessage: boolean = false,
    id: string = ''
  ) => {
    let message = inputMessage || query || '';
    if (isFirstMessage) message = detailsText;
    const noMessage = !message.trim();

    const valid = isFirstMessage
      ? uploadedFiles?.length || selectedContexts?.length
      : contextState.uploadedFiles?.length || contextState.selectedContexts?.length;

    const useCaseId = id && isFirstMessage ? id : chatId;

    if ((noMessage && !valid) || isSending[useCaseId]) return;

    const currentMessage = message;
    setInputMessage('');
    setIsSending((prev) => ({ ...prev, [useCaseId]: true }));

    const selectedContextsArr = contextState.selectedContexts.reduce((acc, contextId) => {
      const contextProcess = contextState.contextProcesses.find((p) => p.id === contextId);
      if (contextProcess?.context_knowledge) {
        acc.push(contextProcess.context_knowledge);
      }
      return acc;
    }, [] as any[]);

    // Create temporary user message to show immediately
    const tempUserMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      query: {
        report: '',
        impact: '',
        user_query: currentMessage,
      },
      answer: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'pending',
      metadata: {},
      timeTaken: 0,
      fileSummary: '',
      files: { data: isFirstMessage ? uploadedFiles : contextState.uploadedFiles },
      context_knowledge: {
        data: isFirstMessage ? selectedContexts : selectedContextsArr || [],
      },
    };

    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      // Mark files as analyzing
      setContextState((prev: any) => ({
        ...prev,
        uploadedFiles: prev.uploadedFiles.map((file: any) => ({
          ...file,
          isAnalyzing: file.filePath && !file.fileData ? true : false,
        })),
      }));

      const filesSelected = isFirstMessage ? uploadedFiles : contextState.uploadedFiles;

      // Analyze files that haven't been analyzed yet
      const filesToAnalyze = (filesSelected || []).filter(
        (file: any) => file.filePath && !file.fileData
      );

      let summary = '';
      let extractedData: any[] = [];

      for (const file of filesToAnalyze) {
        const analysisResponse = await analyzeFile(
          file.filePath || file.url || '',
          file.name,
          file.id,
          file.fileSize || ''
        );

        if (!isValidAnalysisResponse(analysisResponse)) {
          console.error('Invalid LLM analysis response:', analysisResponse);
          ShowToast({
            message: `LLM returned invalid analysis for file "${file.name}"`,
            type: ToastTypeEnum.Error,
          });
          throw new Error(`LLM returned invalid analysis for file "${file.name}"`);
        }

        summary = analysisResponse.final_summary;
        extractedData = [...extractedData, ...analysisResponse.extracted_data];

        // Update file with analysis data
        setContextState((prev: any) => ({
          ...prev,
          uploadedFiles: prev.uploadedFiles.map((f: UploadedFile) =>
            f.id === file.id
              ? {
                  ...f,
                  isAnalyzing: false,
                  finalSummary: analysisResponse.final_summary,
                  extractedData: analysisResponse.extracted_data,
                  fileData: analysisResponse.extracted_data?.[0]?.fileData,
                  fileSize: analysisResponse.extracted_data?.[0]?.fileSize || file.fileSize,
                }
              : f
          ),
          fileSummary: analysisResponse.final_summary,
        }));
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      const currentContextState = contextState;

      // Prepare files data from contextState (now with analyzed data)
      const filesToSend = (extractedData || [])?.map((file: any) => ({
        id: file.id,
        fileName: file.fileName,
        fileUrl: file.fileUrl || '',
        fileData: file.fileData || '',
        fileSize: file.fileSize || '',
        fileSummary: summary || '',
      }));

      // Prepare contexts from contextState
      const contextsToSend = isFirstMessage
        ? selectedContexts || []
        : [...allContextData, ...selectedContextsArr];
      const userFilesToSend = isFirstMessage
        ? filesToSend || []
        : [...allFilesData, ...filesToSend];

      setAllFilesData(userFilesToSend);

      const report =
        versionData && Object.keys(versionData).length > 0
          ? versionData[Object.keys(versionData)[Object.keys(versionData).length - 1]]?.summary
          : '';

      let userQuery: any = {
        report: report,
        user_query: currentMessage,
        context: contextsToSend,
        taxonomy: taxonomy,
        user_file_data: userFilesToSend,
      };

      if (selectedContextsArr?.length) {
        const contextsNames = selectedContextsArr
          ?.map((context) => context?.use_case_title)
          ?.join(', ');
        userQuery.user_query = `${userQuery.user_query ? `${userQuery.user_query}.` : ''} In this query, user has added new file(s) under ${contextsNames}`;
      }

      if (filesToSend?.length) {
        const fileNames = filesToSend?.map((file: any) => file?.fileName)?.join(', ');
        userQuery.user_query = `${userQuery.user_query ? `${userQuery.user_query}.` : ''} In this query, user has provided new or updated context, named ${fileNames}`;
      }

      //these line of code have been commented because it sends the current, expected, specific idea and kpi data in payload
      // if (currentField === 'expectedImpact') {
      //   userQuery['current_process'] = currentProcess || '';
      // }

      // if (currentField === 'specificIdeasOfImprovement') {
      //   userQuery['current_process'] = currentProcess || '';
      //   userQuery['expected_impact'] = expectedImpact || '';
      // }

      // if (currentField === 'keyPerformanceIndicators') {
      //   userQuery['current_process'] = currentProcess || '';
      //   userQuery['expected_impact'] = expectedImpact || '';
      //   userQuery['ideas_of_improvement'] = specificIdeasOfImprovement || '';
      // }

      const useCaseId = id && isFirstMessage ? id : chatId;

      if (isFirstMessage && id && !chatId) {
        setChatId(id);
      }
      if (isFirstMessage && useCaseId)
        navigate(`/data-xplr/${useCaseId}?selectedCategory=${currentField}`);
      const payload = {
        useCaseId: useCaseId,
        query: userQuery,
        domain: useCaseDetails?.domain || '',
        function: useCaseDetails?.function || '',
        department: useCaseDetails?.process_group || '',
        subDepartment: useCaseDetails?.process || '',
        files: isFirstMessage ? uploadedFiles : filesToSend || [],
        context: isFirstMessage ? selectedContexts : selectedContextsArr || [],
        tenantId: '6764029052789d00255f17a1',
        field_type: fieldAPIKeyMap[currentField],
        file_summary: isFirstMessage
          ? summary || finalSummary
          : summary || currentContextState?.fileSummary,
        organization_id: organization_id,
      };

      // Clear uploaded files and contexts after successful send
      setContextState((prev) => ({
        ...prev,
        uploadedFiles: [],
        selectedContexts: [],
      }));

      const data = await postQuery(payload);
      loadSessions(getFieldTypeFromCategory(selectedCategory));

      if (!data.queryId) {
        throw new Error('Failed to send message');
      }

      if (data?.sessionName) {
        setUseCaseDetails({
          ...useCaseDetails,
          title: data?.sessionName || '',
        });
      }

      // Update the message with the actual response
      setMessages((prev) => {
        const messages = [...prev];
        const lastMessage = messages[messages.length - 1];

        if (lastMessage && lastMessage.id.startsWith('temp-')) {
          messages[messages.length - 1] = {
            id: data.queryId || lastMessage.id,
            query: {
              report: '',
              impact: '',
              user_query: currentMessage,
            },
            answer: data.botMessage || '',
            discoveryContent: {
              report: data?.discoveryContent?.report || '',
              summary: data?.discoveryContent?.file_summary || '',
            },
            impact: data?.impact || '',
            version: data.version || '',
            created_at: lastMessage.created_at,
            updated_at: new Date().toISOString(),
            status: 'completed',
            metadata: {
              useCaseId: payload.useCaseId,
              discoveryContent: data.discoveryContent,
              sessionName: data.sessionName,
              report: data.report,
            },
            files: { data: isFirstMessage ? uploadedFiles : filesToSend || [] },
            timeTaken: 0,
            fileSummary: data?.discoveryContent?.file_summary || '',
            context_knowledge: {
              data: isFirstMessage ? selectedContexts : selectedContextsArr || [],
            },
          };
        }

        return messages;
      });

      //if (isFirstMessage && useCaseId) navigate(`/data-xplr/${useCaseId}`);

      setIsSending((prev) => ({ ...prev, [useCaseId]: false }));
    } catch (error: any) {
      console.error('Error sending message:', error);

      const isLLMError =
        error?.message?.includes('Invalid analysis') || error?.message?.includes('LLM');

      ShowToast({
        message: isLLMError
          ? 'We could not correctly understand your uploaded file. Please try again.'
          : 'Failed to send message. Please try again.',
        type: ToastTypeEnum.Error,
      });

      // Store failed message data for restoration
      setFailedMessageData({
        message: currentMessage,
        files: isFirstMessage ? uploadedFiles : contextState.uploadedFiles,
        contexts: isFirstMessage ? selectedContexts : contextState.selectedContexts,
      });

      // Restore input message
      setInputMessage(currentMessage);

      // Restore files and contexts in contextState
      setContextState((prev: any) => ({
        ...prev,
        uploadedFiles: isFirstMessage ? uploadedFiles : contextState.uploadedFiles,
        selectedContexts: isFirstMessage ? selectedContexts : contextState.selectedContexts,
      }));

      // Remove the temporary message from the chat
      setMessages((prev) => prev.filter((msg) => !msg.id.startsWith('temp-')));

      // Show error toast
      ShowToast({
        message: 'Failed to send message. Please try again.',
        type: ToastTypeEnum.Error,
      });

      setIsSending((prev) => ({ ...prev, [useCaseId]: false }));
    }
  };

  const createVersionData = () => {
    const versionDataNew: Record<string, any> = {};
    messages?.forEach((message: ChatMessage) => {
      if (
        message?.discoveryContent &&
        Object.keys(message?.discoveryContent || {})?.length > 0 &&
        message?.version &&
        message?.discoveryContent?.report?.length > 0
      ) {
        versionDataNew[message?.version] = {
          summary: message?.discoveryContent?.report || '',
          impact: message?.impact || '',
        };
      }
    });
    setversionData(versionDataNew);
    let versionKeys = Object.keys(versionDataNew || {});
    if (versionKeys?.length > 0) {
      let version = versionKeys?.[versionKeys?.length - 1];
      handleVersionChange(version);
    }

    // Store versioned reports in Redux for IdeationXPLR
    if (Object.keys(versionData).length > 0 && chatId) {
      dispatch(
        setVersionedReports({
          usecaseId: chatId,
          versionedReports: versionData,
        })
      );
    }
  };

  const getPreviousChat = async (id: string) => {
    if (!id) return;

    setReportLoading(true);
    try {
      const chatData = await getChatHistory(id, fieldAPIKeyMap[currentField]);

      if (chatData?.total && chatData.total > 0) {
        const lastItem = chatData.data?.[chatData.data.length - 1];

        const context: any[] = lastItem?.query?.context ?? [];
        const files: any[] = lastItem?.query?.user_file_data ?? [];

        setAllContextData(context);
        setAllFilesData(files);
        setMessages(chatData.data);

        if (chatData?.parent_metadata?.session_name) {
          setUseCaseDetails((prev) => ({
            ...prev,
            title: chatData?.parent_metadata?.session_name || '',
          }));
        }
      } else if (chatData?.total === 0) {
        setAllContextData(selectedContexts);
        setInputMessage(useCaseDetails?.use_case || '');
        await handleSendMessage(useCaseDetails?.use_case || detailsText, true, id);
      }
    } catch (error) {
      console.error('Error fetching previous chat:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const getUseCaseData = async (id: string) => {
    const useCaseData = await getUseCaseDetails(id);

    if (useCaseData) {
      setUseCaseDetails(useCaseData);
    }
  };
  // const useDataXplrFieldLabels: Partial<Record<OpportunityDetailFieldKey, string>> = useMemo(
  //   () => ({
  //     currentProcess: 'Current Process',
  //     expectedImpact: 'Expected Impact',
  //     specificIdeasOfImprovement: 'Specific Ideas of Improvement',
  //     keyPerformanceIndicators: 'Key Performance Information',
  //   }),
  //   []
  // );

  // const getUseDataXplrFieldLabel = (field: OpportunityDetailFieldKey | null): string => {
  //   if (!field) {
  //     return 'this field';
  //   }
  //   return useDataXplrFieldLabels[field] ?? 'this field';
  // };

  const handleSendUseCaseData = async () => {
    const versions = Object.keys(versionData);
    const latestVersionNumber = versions?.sort(compareVersions).at(-1);
    if (isSendingToSolutionXPLR) return;
    setIsSendingToSolutionXPLR(true);
    try {
      dispatch(
        updateIdeationFieldState({
          field: currentField as keyof NewIdeationState,
          value: editorContent,
        })
      );
      const chatDetails: Record<string, any> = {
        ...recent_imported_chat,
        [chatBotFieldMap[currentField]]: {
          chat_id: chatId,
          version_id: latestVersionNumber,
        },
      };
      dispatch(
        updateIdeationFieldState({
          field: 'recent_imported_chat',
          value: chatDetails,
        })
      );
    } catch (error) {
      console.error('Error sending to Solution XPLR:', error);
      ShowToast({
        message: 'Failed to send to Solution XPLR',
        type: ToastTypeEnum.Error,
      });
    } finally {
      setIsSendingToSolutionXPLR(false);
    }
  };

  const resetData = (showDashboard: boolean = false) => {
    setMessages([]);
    setversionData([]);
    setChatId('');
    setInputMessage('');
    setUseCaseDetails({});
    setShowKnowledgeCreation(showDashboard);
    setCurrentVersion('');
    setContextState({
      details: '',
      uploadedFiles: [],
      selectedContexts: [],
      recentProcesses: [],
      isLoadingRecents: true,
      contextProcesses: [],
      isLoadingContext: false,
      fileSummary: '',
    });
    setAllContextData([]);
    setAllFilesData([]);
  };

  const handleNewChat = () => {
    if (isSending[chatId] === true) {
      ShowToast({
        message: 'Please wait, we are processing your request.',
        type: ToastTypeEnum.Warn,
      });
      return;
    }
    resetData(true);
    navigate('/data-xplr');
  };

  const handleNewIdeation = () => {
    if (isSending[chatId] === true) {
      ShowToast({
        message: 'Please wait, we are processing your request.',
        type: ToastTypeEnum.Warn,
      });
      return;
    }
    setShowKnowledgeCreation(true);
    setChatId('');
  };

  useEffect(() => {
    if (skipChatLoadRef.current) {
      skipChatLoadRef.current = false;
      return;
    }

    // if (chatId) {
    //   getPreviousChat(chatId);
    // }
  }, [chatId]);
  // removed initialChatId from dependencies to avoid double fetch

  useEffect(() => {
    if (messages?.length) {
      createVersionData();
    }
  }, [messages]);

  useEffect(() => {
    if (location.state?.reset) {
      skipChatLoadRef.current = true;
      handleNewChat();
      handleNewIdeation();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);
  // added location.state.reset to dependencies to handle reset on navigation

  const updateChats = () => {
    getUseCaseData(chatId);
    getPreviousChat(chatId);
  };

  const handleBackClick = () => {
    const versions = Object.keys(versionData);
    const latestVersionNumber = versions?.sort(compareVersions).at(-1);

    const currFieldData = recent_imported_chat?.[chatBotFieldMap?.[currentField]];
    const hasLatestData = currFieldData?.chat_id
      ? currFieldData?.version_id === latestVersionNumber
      : false;
    if (
      versionData &&
      Object.keys(versionData)?.length > 0 &&
      (!currFieldData?.chat_id || !hasLatestData)
    ) {
      setIsUseDataXplrModalOpen(true);
    } else {
      handleBack?.();
    }
  };

  const getFieldTypeFromCategory = (category: string): string | undefined => {
    if (category === 'All') {
      return undefined;
    }
    const categoryToFieldMap: Record<string, string> = {
      'Current Process': fieldAPIKeyMap.currentProcess,
      'Expected Impact': fieldAPIKeyMap.expectedImpact,
      'Key Performance Indicators': fieldAPIKeyMap.keyPerformanceIndicators,
      'Specific Idea of Improvement': fieldAPIKeyMap.specificIdeasOfImprovement,
    };
    return categoryToFieldMap[category];
  };

  const getFieldKeyFromAPIType = (apiType: string): OpportunityDetailFieldKey => {
    const apiToFieldKeyMap: Record<string, OpportunityDetailFieldKey> = {
      [fieldAPIKeyMap.currentProcess]: 'currentProcess',
      [fieldAPIKeyMap.expectedImpact]: 'expectedImpact',
      [fieldAPIKeyMap.specificIdeasOfImprovement]: 'specificIdeasOfImprovement',
      [fieldAPIKeyMap.keyPerformanceIndicators]: 'keyPerformanceIndicators',
      current_process: 'currentProcess',
      expected_impact: 'expectedImpact',
      ideas_of_improvement: 'specificIdeasOfImprovement',
      key_performance_indicator: 'keyPerformanceIndicators',
    };
    return apiToFieldKeyMap[apiType] || 'currentProcess';
  };

  const getFieldKeyFromCategory = (category: string): OpportunityDetailFieldKey => {
    const categoryToFieldKeyMap: Record<string, OpportunityDetailFieldKey> = {
      'Current Process': 'currentProcess',
      'Expected Impact': 'expectedImpact',
      KPIs: 'keyPerformanceIndicators',
      'Specific Idea of Improvement': 'specificIdeasOfImprovement',
      current_process: 'currentProcess',
      expected_impact: 'expectedImpact',
      ideas_of_improvement: 'specificIdeasOfImprovement',
      key_performance_indicator: 'keyPerformanceIndicators',
    };
    return categoryToFieldKeyMap[category] || 'currentProcess';
  };

  // Handler for field changes from ProcessCreation
  const handleFieldChange = (apiField: string) => {
    const fieldKey = getFieldKeyFromAPIType(apiField);
    setCurrentField(fieldKey);
  };

  const handleSortChange = async (newSortBy: string) => {
    setSortBy(newSortBy);
    await loadSessions(getFieldTypeFromCategory(selectedCategory), newSortBy);
  };

  // Handlers for category and search changes
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    const fieldKey = getFieldKeyFromCategory(category);
    setCurrentField(fieldKey);
    setSearchParams({ selectedCategory: fieldKey });
  };

  useEffect(() => {
    if (location.state?.fromDrawer && location.state?.selectedCategory) {
      const fieldKey = getFieldKeyFromCategory(location.state.selectedCategory);
      setCurrentField(fieldKey);

      navigate(location.pathname, { replace: true, state: {} });
    }

    if (searchParams.get('searchQuery')) {
      setSearchQuery(searchParams.get('searchQuery') || '');
    }
  }, [location.state, location.pathname, navigate]);

  const handleSelectChat = (selectedChatId: string, sessionFieldType?: string) => {
    if (selectedChatId === chatId) return;
    if (isSending[chatId] === true) {
      ShowToast({
        message: 'Please wait, we are processing your request.',
        type: ToastTypeEnum.Warn,
      });
      return;
    }
    setShowKnowledgeCreation(false);
    resetData();
    setChatId(selectedChatId);
    skipChatLoadRef.current = false;

    // If the session has a specific field_type, use it
    if (sessionFieldType) {
      const fieldKey = getFieldKeyFromAPIType(sessionFieldType);
      setCurrentField(fieldKey);
    }

    const field = getFieldKeyFromCategory(sessionFieldType || selectedCategory);
    navigate(`/data-xplr/${selectedChatId}?selectedCategory=${field}&searchQuery=${searchQuery}`, {
      state: { skipChat: true },
    });
  };

  const handleSearchChange = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };

  useEffect(() => {
    loadSessions(getFieldTypeFromCategory(selectedCategory));
  }, [searchQuery]);

  useEffect(() => {
    loadSessions(getFieldTypeFromCategory(selectedCategory));
  }, [searchQuery]);

  const loadSessions = async (
    fieldType?: string,
    sortFilter?: string,
    append: boolean = false,
    limit?: number
  ) => {
    try {
      if (append) {
        setIsLoadingMore(true);
        setHasReachedEnd(false);
      }

      const sort = sortFilter || sortBy;
      const currentSessionCount = chatSessions.length;
      const sessions = await getLatestQueryHistory(
        limit || currentLimit,
        organization_id,
        fieldType,
        false,
        searchQuery,
        sort
      );
      if (sessions?.data) {
        let sessionList = (sessions?.data || []).map((item: any) => ({
          id: item.id,
          use_case_title:
            item.session_name || item.ideation_intake[0]?.ideation_name || 'Untitled Session',
          session_name: item.session_name,
          created_by: item.created_by_user_name || 'admin',
          updated_by: item.updated_by_user_name || '',
          created_at: item.created_at,
          updated_at: item.updated_at,
          usedInProcess: item.field_type
            ? fieldLabelMap
              ? fieldLabelMap[item.field_type] || item.field_type
              : item.field_type
            : 'Unknown',
          conversation_id: item.conversation_id,
          ideation_intake: item.ideation_intake || [],
          field_type: item.field_type,
          highlight: item.highlight,
        }));
        // Apply client-side search filtering (also consider highlight text)
        if (searchQuery.trim() && !append) {
          const query = searchQuery.toLowerCase();
          sessionList = sessionList.filter((session) => {
            const titleMatches = session.use_case_title?.toLowerCase().includes(query);
            const nameMatches = session.session_name
              ? session.session_name.toLowerCase().includes(query)
              : false;
            const highlightMatches = session.highlight
              ? String(session.highlight).toLowerCase().includes(query)
              : false;

            return titleMatches || nameMatches || highlightMatches;
          });
        }

        setChatSessions((prev) => {
          const existingIds = new Set(append ? prev.map((s) => s.id) : []);
          const newSessions = sessionList.filter((s) => !existingIds.has(s.id));
          const updatedSessions = append ? [...prev, ...newSessions] : sessionList;

          // Check if we reached the end (no new sessions loaded)
          if (append && updatedSessions.length === currentSessionCount) {
            setHasReachedEnd(true);
          }

          return updatedSessions;
        });
      }
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    } finally {
      if (append) setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && !hasReachedEnd) {
      const newLimit = currentLimit + 20;
      setCurrentLimit(newLimit);
      loadSessions(getFieldTypeFromCategory(selectedCategory), sortBy, true, newLimit);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      setCurrentLimit(20);
      setHasReachedEnd(false);
      loadSessions(getFieldTypeFromCategory(selectedCategory));
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      setCurrentLimit(20);
      setHasReachedEnd(false);
      loadSessions(getFieldTypeFromCategory(selectedCategory));
    }
  }, [searchQuery]);

  useEffect(() => {
    if (!initialChatId) return;

    setShowKnowledgeCreation(false);
    setChatId(initialChatId);
    getPreviousChat(initialChatId);
  }, [initialChatId]);

  useEffect(() => {
    // Clear failed message data when a successful message is sent
    if (messages.length > 0 && failedMessageData) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.status === 'completed') {
        setFailedMessageData(null);
      }
    }
  }, [messages]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        key={`coi-usecase-${chatId || 'new'}`}
        sx={{
          display: 'flex',
          height: '93vh',
          // backgroundColor: '#fafafa',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: 0,
          }}
        >
          <ChatListingSidebar
            sessions={chatSessions}
            currentChatId={chatId}
            onSelectChat={handleSelectChat}
            onNewChat={handleNewChat}
            onNewIdeation={handleNewIdeation}
            onCategoryChange={handleCategoryChange}
            onSearchChange={handleSearchChange}
            onRefetch={() => loadSessions(getFieldTypeFromCategory(selectedCategory))}
            selectedCategory={selectedCategory}
            setuseCaseDetails={setUseCaseDetails}
            useCaseDetails={useCaseDetails}
            onLoadMore={handleLoadMore}
            isLoadingMore={isLoadingMore}
            hasReachedEnd={hasReachedEnd}
            searchFromQuery={searchQuery}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onSortChange={handleSortChange}
          />
          {showKnowledgeCreation ? (
            <>
              <ThemeProvider theme={theme}>
                <ProcessCreation
                  field={currentField}
                  handleForward={(id: string) => {
                    navigate(`/data-xplr/${id}?selectedCategory=${currentField}`);
                  }}
                  sessions={chatSessions}
                  currentChatId={chatId}
                  onSelectChat={handleSelectChat}
                  onNewChat={handleNewChat}
                  onNewIdeation={handleNewIdeation}
                  setShowKnowledgeCreation={setShowKnowledgeCreation}
                  onFieldChange={handleFieldChange}
                />
              </ThemeProvider>
            </>
          ) : (
            initialChatId && (
              <>
                <CoiChat
                  width={chatWidth}
                  onWidthChange={handleChatWidthChange}
                  onVersionChange={handleVersionChange}
                  currentVersion={currentVersion}
                  handleSendMessage={handleSendMessage}
                  messages={messages}
                  setMessages={setMessages}
                  isSending={isSending[chatId] || false}
                  inputMessage={inputMessage}
                  setInputMessage={setInputMessage}
                  useCaseDetails={useCaseDetails}
                  versionData={versionData}
                  handleBack={handleBackClick}
                  field={currentField}
                  setContextState={setContextState}
                  contextState={contextState}
                  setChatId={setChatId}
                  initialChatId={initialChatId}
                  chatId={chatId}
                  setuseCaseDetails={setUseCaseDetails}
                  onRefetchSessions={() => loadSessions(getFieldTypeFromCategory(selectedCategory))}
                  handleNewChat={() => resetData(true)}
                />
                <Divider orientation="vertical" sx={{ border: '3px solid #EBEBEB' }} />
                <CoiUseCase
                  useCaseId={chatId}
                  selectedVersion={currentVersion}
                  onVersionChange={handleVersionChange}
                  versionData={versionData}
                  useCaseDetails={useCaseDetails}
                  updateChats={updateChats}
                  isSending={isSending[chatId] || false}
                  field={currentField}
                  setActiveStep={(_step: string) => {}}
                  fileSummary={fileSummary}
                  editorContent={editorContent}
                  setEditorContent={setEditorContent}
                  isSendingToSolutionXPLR={isSendingToSolutionXPLR}
                  setIsSendingToSolutionXPLR={setIsSendingToSolutionXPLR}
                  handleSendUseCaseData={handleSendUseCaseData}
                  reportLoading={reportLoading}
                />
              </>
            )
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DataXPLR;
